import { useEffect, useRef, useState } from "react";
import { useI18n } from "@/lib/i18n";

declare global {
  interface Window {
    turnstile?: {
      render: (
        el: HTMLElement,
        opts: {
          sitekey: string;
          callback: (token: string) => void;
          "expired-callback"?: () => void;
          "error-callback"?: () => void;
          "timeout-callback"?: () => void;
          theme?: "light" | "dark" | "auto";
          language?: string;
          size?: "normal" | "compact" | "flexible" | "invisible";
          appearance?: "always" | "execute" | "interaction-only";
          execution?: "render" | "execute";
          "refresh-expired"?: "auto" | "manual" | "never";
          "retry"?: "auto" | "never";
          "retry-interval"?: number;
        }
      ) => string;
      reset: (id?: string) => void;
      remove: (id?: string) => void;
      execute: (idOrEl?: string | HTMLElement) => void;
    };
  }
}

const SCRIPT_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js";
const SITE_KEY = (import.meta as any).env?.VITE_TURNSTILE_SITE_KEY as string | undefined;

let scriptPromise: Promise<void> | null = null;

function loadTurnstileScript(): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  const promise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src^="${SCRIPT_URL}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("Turnstile script failed")));
      if ((existing as any).__loaded) resolve();
      return;
    }
    const s = document.createElement("script");
    s.src = `${SCRIPT_URL}?render=explicit`;
    s.async = true;
    s.defer = true;
    s.onload = () => {
      (s as any).__loaded = true;
      resolve();
    };
    s.onerror = () => {
      try { s.remove(); } catch {}
      reject(new Error("Turnstile script failed"));
    };
    document.head.appendChild(s);
  });
  // Clear cached promise on failure so subsequent mounts can retry without a page reload.
  promise.catch(() => {
    if (scriptPromise === promise) scriptPromise = null;
  });
  scriptPromise = promise;
  return promise;
}

interface Props {
  onToken: (token: string) => void;
  onExpire?: () => void;
  /**
   * When true, suppress the visible error label. Used by the global
   * background widget mounted on every page — failures there should not
   * leak a red error message into the corner of unrelated pages
   * (landing, pricing, etc.). The login popup mounts its own non-silent
   * instance so users still see the error when they're actually trying
   * to sign in.
   */
  silent?: boolean;
  /**
   * Cloudflare widget appearance. Default is "always" — render the
   * checkbox inline so users always see the security check on
   * sensitive forms (login, OTP verify). Pass "interaction-only" for
   * the background pre-warm widget that should only paint when a real
   * challenge is required.
   */
  appearance?: "always" | "execute" | "interaction-only";
  /** Widget layout — "normal" gives the full 300×65 checkbox. */
  size?: "normal" | "compact" | "flexible" | "invisible";
}

export function TurnstileWidget({
  onToken,
  onExpire,
  silent = false,
  appearance = "always",
  size = "normal",
}: Props) {
  const { lang } = useI18n();
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Watch for Cloudflare painting a real interactive challenge. The host
  // wrapper (a parent with `data-tn-host`) is hidden by default; we flip
  // it visible only when an iframe with non-zero size is injected — that
  // way the silent fast path stays invisible to the user, and only a real
  // challenge surfaces. Only the background widgets that opt-in via a
  // `data-tn-host` parent participate; inline visible widgets (login
  // popup, OTP step) skip this entirely.
  useEffect(() => {
    if (appearance !== "interaction-only") return;
    const node = containerRef.current;
    if (!node) return;
    const host = node.closest<HTMLElement>("[data-tn-host]");
    if (!host) return;
    const reveal = (visible: boolean) => {
      host.style.opacity = visible ? "1" : "0";
      host.style.pointerEvents = visible ? "auto" : "none";
    };
    const check = () => {
      const iframe = node.querySelector("iframe");
      const hasUI = !!iframe && (iframe.clientHeight > 0 || iframe.clientWidth > 0);
      reveal(hasUI);
    };
    const obs = new MutationObserver(check);
    obs.observe(node, { childList: true, subtree: true, attributes: true, attributeFilter: ["style", "class", "width", "height"] });
    const interval = window.setInterval(check, 1000);
    return () => {
      obs.disconnect();
      window.clearInterval(interval);
      reveal(false);
    };
  }, [appearance]);

  useEffect(() => {
    if (!SITE_KEY) return;
    let cancelled = false;
    loadTurnstileScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: SITE_KEY,
          theme: "light",
          language: lang === "ar" ? "ar" : "en",
          size,
          appearance,
          "refresh-expired": "auto",
          retry: "auto",
          "retry-interval": 8000,
          callback: (token: string) => onToken(token),
          "expired-callback": () => {
            onExpire?.();
            // Auto-refresh — most users will get a new token instantly
            // without seeing the widget.
            try {
              if (widgetIdRef.current && window.turnstile) {
                window.turnstile.reset(widgetIdRef.current);
              }
            } catch {}
          },
          "timeout-callback": () => {
            try {
              if (widgetIdRef.current && window.turnstile) {
                window.turnstile.reset(widgetIdRef.current);
              }
            } catch {}
          },
          "error-callback": () => {
            if (!silent) {
              setError(lang === "ar" ? "تعذّر تحميل التحقق" : "Verification failed to load");
            }
          },
        });
      })
      .catch(() => {
        if (!silent) {
          setError(lang === "ar" ? "تعذّر الاتصال بخدمة التحقق" : "Could not reach verification service");
        }
      });
    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {}
        widgetIdRef.current = null;
      }
    };
  }, [lang, onToken, onExpire]);

  if (!SITE_KEY) {
    return null;
  }

  // With `appearance: "interaction-only"` the widget is invisible unless a
  // real challenge is needed; render a zero-height container so it doesn't
  // reserve vertical space in the popup.
  return (
    <div className="flex justify-center">
      <div ref={containerRef} />
      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
    </div>
  );
}

export const isTurnstileConfigured = !!SITE_KEY;

/**
 * Preload the Turnstile script as soon as the app boots so it's already
 * parsed + executed by the time the user opens the login popup. Without
 * this the script download (~50KB gzipped) only starts on first popup
 * open, adding 300-1200ms to the perceived widget render time on slow
 * networks. Safe to call multiple times — internally deduped by
 * `scriptPromise`.
 */
export function preloadTurnstile(): void {
  if (!SITE_KEY) return;
  // Fire-and-forget; failures are silently retried by the widget itself
  // when it actually mounts.
  loadTurnstileScript().catch(() => {});
}
